import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useMemo, useState } from "react";
import { CheckSquare, Loader2 } from "lucide-react";
import { authorizedApiClient } from "../../../services/config";
import { getTaskBoardCounts } from "../../../services/tasksEndpoints";
import { allWorkspaceMembers } from "../../../services/workspaceMemberEndpoint";
import type {
  WorkspaceObjectSearchRequest,
  WorkspaceObjectSearchResponse,
} from "../../../services/workspace/workspaceEndpoints.interface";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import TasksBoard from "./components/TasksBoard";
import TasksHeader from "./components/TasksHeader";
import TasksListTable from "./components/TasksListTable";
import TaskDetailSheet from "./components/TaskDetailSheet";
import type {
  BoardStatus,
  TaskBoardStatusFilter,
  TaskDisplayMode,
  TaskPriorityFilter,
  TaskRecord,
} from "./tasksTypes";

const TASK_SELECT_FIELDS = [
  "subject",
  "description",
  "native_subject",
  "native_description",
  "status",
  "priority",
  "board_status",
  "owner_side",
  "due_date",
  "assigned_to",
  "evidence_text",
  "source_type",
  "task_type",
  "source_external_id",
] as const;

const withObjectIncludes = (payload: WorkspaceObjectSearchRequest): WorkspaceObjectSearchRequest => ({
  ...payload,
  include: [
    {
      object_type: "accounts",
      select: ["name", "domain", "arr", "mrr", "subscription_status"],
      limit: 1,
    },
  ],
});

const deriveStatusFromOwnerSide = (ownerSide?: string): string => {
  switch (ownerSide?.toLowerCase()) {
    case "vendor": return "on_us";
    case "customer": return "on_them";
    case "shared": return "investigating";
    default: return "investigating";
  }
};

const parseTaskRecords = (
  data: WorkspaceObjectSearchResponse["data"],
): TaskRecord[] =>
  data.map((record) => {
    const fields = record.fields || {};
    const relatedAccounts = record.related?.accounts || [];
    const account = relatedAccounts[0];

    return {
      id: record.id,
      object_id: record.object_id || undefined,
      external_id: record.id,
      subject: fields.subject || fields.native_subject || fields.description || fields.native_description,
      description: fields.description || fields.native_description,
      native_subject: fields.native_subject,
      native_description: fields.native_description,
      status: fields.status,
      priority: fields.priority,
      board_status: (fields.board_status || deriveStatusFromOwnerSide(fields.owner_side)) as BoardStatus,
      owner_side: fields.owner_side,
      due_date: fields.due_date,
      assigned_to: fields.assigned_to,
      evidence_text: fields.evidence_text,
      source_type: fields.source_type,
      task_type: fields.task_type,
      source_external_id: fields.source_external_id,
      account_name: account?.name || account?.fields?.name,
      account_domain: account?.domain || account?.fields?.domain,
      account_external_id: account?.id,
      account_arr: (account?.arr ?? account?.fields?.arr) != null ? Number(account?.arr ?? account?.fields?.arr) : undefined,
      account_mrr: (account?.mrr ?? account?.fields?.mrr) != null ? Number(account?.mrr ?? account?.fields?.mrr) : undefined,
      account_subscription_status: account?.subscription_status || account?.fields?.subscription_status,
      created_at: fields.created_at,
      updated_at: fields.updated_at,
    };
  });

const TasksPage = () => {
  const workspace = useWorkspaceStore((state) => state.workspace);

  const [displayMode, setDisplayMode] = useState<TaskDisplayMode>("board");
  const [showFilters, setShowFilters] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [priorityFilter, setPriorityFilter] = useState<TaskPriorityFilter>("all");
  const [boardStatusFilter, setBoardStatusFilter] = useState<TaskBoardStatusFilter>("all");
  const [accountFilter, setAccountFilter] = useState<string>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");
  const [selectedTask, setSelectedTask] = useState<TaskRecord | null>(null);

  // Fetch workspace members once for assignee resolution
  const membersQuery = useQuery({
    queryKey: ["workspace-members", workspace?.id],
    queryFn: () => allWorkspaceMembers({ workspace_id: workspace!.id }),
    enabled: !!workspace?.id,
    staleTime: 60_000,
  });

  const members: any[] = membersQuery.data?.data?.members ?? [];

  // Lookup: member_id -> { name, email, first_name, last_name }
  const memberLookup = useMemo(() => {
    const map: Record<string, { name: string; email: string; initials: string }> = {};
    for (const m of members) {
      const name = `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.email;
      const initials = (m.first_name?.[0] || m.email?.[0] || "?").toUpperCase();
      const entry = { name, email: m.email, initials };
      // assigned_to can be a member_id OR a user_id (extracted tasks use user_id) — index both, plus email
      if (m.member_id) map[m.member_id] = entry;
      if (m.user_id) map[m.user_id] = entry;
      if (m.email) map[m.email] = entry;
    }
    return map;
  }, [members]);

  const tasksQuery = useQuery<TaskRecord[]>({
    queryKey: ["tasks-page", workspace?.id],
    enabled: !!workspace?.id,
    queryFn: async () => {
      const workspaceId = workspace?.id;
      if (!workspaceId) return [];

      const payload: WorkspaceObjectSearchRequest = {
        object_type: "tasks",
        select: [...TASK_SELECT_FIELDS],
        sort: [{ field: "created_at", order: "desc" }],
        limit: 200,
      };

      const endpoint = `/workspace/${workspaceId}/object-search`;

      try {
        const response = await authorizedApiClient.post<WorkspaceObjectSearchResponse>(
          endpoint,
          withObjectIncludes(payload),
        );
        return parseTaskRecords(response.data?.data || []);
      } catch (error: unknown) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;

        if (status === 400 || status === 422) {
          const fallback = await authorizedApiClient.post<WorkspaceObjectSearchResponse>(
            endpoint,
            payload,
          );
          return parseTaskRecords(fallback.data?.data || []);
        }

        throw error;
      }
    },
  });

  const boardCountsQuery = useQuery({
    queryKey: ["task-board-counts", workspace?.id],
    enabled: !!workspace?.id,
    queryFn: async () => {
      if (!workspace?.id) return null;
      return getTaskBoardCounts({ workspace_id: workspace.id });
    },
  });

  const filteredTasks = useMemo(() => {
    const tasks = tasksQuery.data || [];
    const normalizedSearch = searchValue.trim().toLowerCase();

    return tasks.filter((task) => {
      if (normalizedSearch) {
        const haystack = [
          task.subject,
          task.description,
          task.native_subject,
          task.native_description,
          task.account_name,
          task.assigned_to,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(normalizedSearch)) return false;
      }

      if (priorityFilter !== "all") {
        if ((task.priority?.toLowerCase() || "") !== priorityFilter) return false;
      }

      if (boardStatusFilter !== "all") {
        if (task.board_status !== boardStatusFilter) return false;
      }

      if (accountFilter !== "all") {
        if (task.account_external_id !== accountFilter) return false;
      }

      if (assigneeFilter !== "all") {
        if (assigneeFilter === "unassigned") {
          if (task.assigned_to) return false;
        } else {
          if (task.assigned_to !== assigneeFilter) return false;
        }
      }

      return true;
    });
  }, [tasksQuery.data, searchValue, priorityFilter, boardStatusFilter, accountFilter, assigneeFilter]);

  return (
    <div className="h-full flex flex-col bg-background">
      <TasksHeader
        displayMode={displayMode}
        onDisplayModeChange={setDisplayMode}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters((prev) => !prev)}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        priorityFilter={priorityFilter}
        onPriorityFilterChange={setPriorityFilter}
        boardStatusFilter={boardStatusFilter}
        onBoardStatusFilterChange={setBoardStatusFilter}
        tasks={tasksQuery.data || []}
        accountFilter={accountFilter}
        onAccountFilterChange={setAccountFilter}
        assigneeFilter={assigneeFilter}
        onAssigneeFilterChange={setAssigneeFilter}
        members={members}
        memberLookup={memberLookup}
      />

      <div className="flex-1 overflow-auto bg-background">
        {tasksQuery.isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : tasksQuery.isError ? (
          <div className="p-6">
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              <p className="font-medium">Unable to load tasks</p>
              <p className="mt-1 text-destructive/80">
                We couldn&apos;t fetch tasks right now. Please try again.
              </p>
            </div>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
            <CheckSquare size={18} className="text-muted-foreground/60" />
            <p className="text-sm font-semibold text-foreground">No tasks found</p>
            <p className="text-xs text-muted-foreground">
              Try changing your filters or search terms.
            </p>
          </div>
        ) : displayMode === "list" ? (
          <TasksListTable tasks={filteredTasks} onTaskClick={setSelectedTask} memberLookup={memberLookup} />
        ) : (
          <TasksBoard tasks={filteredTasks} onTaskClick={setSelectedTask} members={members} memberLookup={memberLookup} />
        )}
      </div>

      {selectedTask && (
        <TaskDetailSheet
          task={selectedTask}
          open={!!selectedTask}
          onClose={() => setSelectedTask(null)}
          members={members}
          memberLookup={memberLookup}
        />
      )}
    </div>
  );
};

export default TasksPage;
